import { ATTRS_VALIDATE_MAP } from "@/utils/attrNameData";

const CANVAS_ERROR_MAP_KEY = "canvasErrorMap";

export function getCanvasErrorMap() {
  try {
    return JSON.parse(sessionStorage.getItem(CANVAS_ERROR_MAP_KEY) || "{}");
  } catch (e) {
    return {};
  }
}

export function setCanvasErrorMap(map) {
  sessionStorage.setItem(CANVAS_ERROR_MAP_KEY, JSON.stringify(map || {}));
}

/** 收集工程内所有组件 id，用于清理幽灵校验 */
export function collectComponentIds(pageDatas) {
  const ids = new Set();
  if (!pageDatas || typeof pageDatas !== "object") {
    return ids;
  }
  Object.keys(pageDatas).forEach((pageId) => {
    const list = pageDatas[pageId];
    if (!Array.isArray(list)) {
      return;
    }
    list.forEach((comp) => {
      const walk = (node) => {
        if (!node) {
          return;
        }
        if (node.id) {
          ids.add(String(node.id));
        }
        if (Array.isArray(node.propValue)) {
          node.propValue.forEach(walk);
        }
      };
      walk(comp);
    });
  });
  return ids;
}

export function pruneCanvasErrorMap(pageDatas) {
  const map = getCanvasErrorMap();
  const validIds = collectComponentIds(pageDatas);
  let changed = false;
  Object.keys(map).forEach((pageId) => {
    if (!map[pageId] || typeof map[pageId] !== "object") {
      delete map[pageId];
      changed = true;
      return;
    }
    Object.keys(map[pageId]).forEach((compId) => {
      if (!validIds.has(String(compId))) {
        delete map[pageId][compId];
        changed = true;
      }
    });
    if (Object.keys(map[pageId]).length === 0) {
      delete map[pageId];
      changed = true;
    }
  });
  if (changed) {
    setCanvasErrorMap(map);
  }
  return map;
}

/** 与属性面板展示规则一致，避免隐藏字段参与校验产生幽灵报错 */
export function shouldValidateField(comp, configKey, key) {
  if (!comp || !key) {
    return false;
  }
  const style = comp.style || {};
  const network = comp.networkConfig || {};

  if (configKey === "style") {
    if (key === "btnLoadingTime") {
      return !!style.btnLoading;
    }
    if (key === "alias") {
      const kqds = style.kqds;
      const needAlias =
        kqds === true || kqds === "true" || kqds === 1 || kqds === "1";
      return comp.component === "v-button" && needAlias;
    }
    if (key === "bindPage") {
      return comp.component === "ChildPage";
    }
    if (key === "pageWidth" || key === "pageHeight") {
      return comp.component === "Back" && !!comp.isChildPage;
    }
    if (key === "backgroundColor" && style.backgroundType && style.backgroundType !== "color") {
      return false;
    }
    if (key === "backgroundImage" && style.backgroundType && style.backgroundType !== "icon") {
      return false;
    }
    if (key === "pressBackgroundColor" && style.pressBackgroundType && style.pressBackgroundType !== "color") {
      return false;
    }
    if (key === "pressBackgroundImage" && style.pressBackgroundType && style.pressBackgroundType !== "icon") {
      return false;
    }
    if (["textScrollDir", "textScrollSpeed"].includes(key)) {
      return !!style.textScroll;
    }
    return true;
  }

  if (configKey === "networkConfig") {
    if (key === "input") {
      return comp.component === "MatrixIn";
    }
    if (key === "output") {
      return comp.component === "MatrixOut";
    }
    if (key === "rulesEngine") {
      return comp.component === "MatrixExecute";
    }
    if (key === "sliderList") {
      return comp.component === "Slider";
    }
    if (["bfqIpAdress", "bfqPort", "rootDirectory"].includes(key)) {
      return comp.component === "Visualization";
    }
    if (key === "directives2" && style.oneClick) {
      return false;
    }
    return !!network && Object.prototype.hasOwnProperty.call(network, key);
  }

  return false;
}

const ATTR_FIELD_LABELS = {
  width: "宽度",
  height: "高度",
  top: "Y",
  left: "X",
  alias: "别名",
  pageName: "页面名称",
  url: "地址",
  interval: "轮播间隔",
  btnLoadingTime: "loading 持续时间",
  ipAdress: "IP地址",
  port: "端口号",
  bindPage: "绑定子页面",
  bfqIpAdress: "IP地址",
  bfqPort: "端口号",
  sliderList: "滑块单元",
  input: "输入位",
  output: "输出位",
  rulesEngine: "规则引擎",
  rootDirectory: "根目录",
  directives: "指令",
  directives2: "二次按压指令",
  pageWidth: "宽",
  pageHeight: "高",
};

export function getAttrFieldLabel(key) {
  if (!key) {
    return "";
  }
  return ATTR_FIELD_LABELS[key] || key;
}

function findCompInList(list, componentId) {
  if (!list || !componentId) {
    return null;
  }
  for (let i = 0; i < list.length; i++) {
    const c = list[i];
    if (c && String(c.id) === String(componentId)) {
      return c;
    }
    if (c && Array.isArray(c.propValue)) {
      const sub = findCompInList(c.propValue, componentId);
      if (sub) {
        return sub;
      }
    }
  }
  return null;
}

export function findFirstCanvasValidationError(errorMap) {
  const map = errorMap || getCanvasErrorMap();
  for (const pageId in map) {
    const pageInfo = map[pageId];
    if (!pageInfo) {
      continue;
    }
    for (const componentId in pageInfo) {
      const attrInfo = pageInfo[componentId];
      if (!attrInfo) {
        continue;
      }
      for (const attrId in attrInfo) {
        const val = attrInfo[attrId];
        if (typeof val === "string" && val) {
          return {
            pageId: String(pageId),
            componentId: String(componentId),
            attrKey: attrId,
            prefixKey: "",
            message: val,
          };
        }
        if (val && typeof val === "object") {
          for (const nestedKey in val) {
            if (val[nestedKey]) {
              return {
                pageId: String(pageId),
                componentId: String(componentId),
                attrKey: nestedKey,
                prefixKey: attrId,
                message:
                  typeof val[nestedKey] === "string"
                    ? val[nestedKey]
                    : "校验失败",
              };
            }
          }
        }
      }
    }
  }
  return null;
}

function walkPageLabel(nodes, pageId) {
  let label = "";
  (nodes || []).forEach((p) => {
    if (String(p.value) === String(pageId)) {
      label = p.label;
    }
    if (!label && p.children && p.children.length) {
      label = walkPageLabel(p.children, pageId);
    }
  });
  return label;
}

export function describeCanvasValidationError(error, ctx = {}) {
  if (!error) {
    return null;
  }
  const { pageOptions, canvasAllData, selectedCurrentPage, componentData } = ctx;
  const pageName =
    walkPageLabel(pageOptions, error.pageId) || String(error.pageId);
  let componentName = String(error.componentId);
  try {
    const data =
      typeof canvasAllData === "string"
        ? JSON.parse(canvasAllData || "{}")
        : canvasAllData || {};
    let list = data[error.pageId] || data[String(error.pageId)] || [];
    if (
      String(error.pageId) === String(selectedCurrentPage) &&
      Array.isArray(componentData) &&
      componentData.length
    ) {
      list = componentData;
    }
    const comp = findCompInList(list, error.componentId);
    if (comp) {
      if (comp.component === "v-button") {
        componentName =
          comp.propValue ||
          (comp.style && comp.style.alias) ||
          comp.label ||
          componentName;
      } else {
        componentName = comp.label || comp.component || componentName;
      }
    }
  } catch (e) {
    /* ignore */
  }
  const attrLabel = getAttrFieldLabel(error.attrKey);
  return {
    ...error,
    pageName,
    componentName,
    attrLabel,
    tabName: inferErrorTab(error),
    summary: `页面「${pageName}」组件「${componentName}」的「${attrLabel}」${error.message}`,
  };
}

export function inferErrorTab(error) {
  if (!error) {
    return "attr";
  }
  if (error.prefixKey && String(error.prefixKey).indexOf("device_") === 0) {
    return "passiveMonitorConfig";
  }
  const networkKeys = [
    "ipAdress",
    "port",
    "directives",
    "directives2",
    "input",
    "output",
    "rulesEngine",
    "sliderList",
    "bfqIpAdress",
    "bfqPort",
    "rootDirectory",
  ];
  if (networkKeys.indexOf(error.attrKey) !== -1) {
    return "networkConfig";
  }
  return "attr";
}

export function hasCanvasValidationErrors(errorMap) {
  return !!findFirstCanvasValidationError(errorMap);
}

export function initEmptyErrorMapForPages(pageIds) {
  const map = {};
  (pageIds || []).forEach((id) => {
    map[String(id)] = {};
  });
  setCanvasErrorMap(map);
  return map;
}

export function getValidateFn(key) {
  return ATTRS_VALIDATE_MAP[key] && ATTRS_VALIDATE_MAP[key].validate;
}
